import { getVariableValue } from "@/shared/utils/resolve-tokens";
import {
  type PillDecorator,
  VARIABLE_PILL_CALC_CLASS,
} from "@/shared/utils/variable-pill";
import { computeCalcPillDisplay } from "./calc-pill-display";
import { parseNumericOperand } from "./parse-numeric-operand";

/** Label every calculation pill carries in the editor (Figma 80:44267). */
export const CALC_PILL_LABEL = "calculation";

/**
 * Builds the `PillDecorator` (see `shared/utils/variable-pill.ts`) that makes
 * a calc-variable's pill read "calculation" (Figma 80:44267 — green-border,
 * mint-fill pill with a Σ corner badge; styling lives in Richtext.scss's
 * `.sd-variable-pill--calc`) and carry the formula string as a hover tooltip
 * (Figma 80:45420 — dark tooltip; rendered from the pill's `title` attribute,
 * set below).
 *
 * Formulas have no user-facing name (the Σ editor has no Name field — Figma
 * 83:45625), so the label is the same fixed word for every calc pill and the
 * formula in the tooltip is what tells them apart. `formulas` comes from
 * `useCalcFormulas()`, keyed by the `{{name}}` token. The second parameter is
 * kept (optional/unused) so existing call sites (Froala.tsx) don't change.
 */
export const createCalcPillDecorator = (
  formulas: Record<string, string>,
  data?: Record<string, unknown> | null,
): PillDecorator => {
  // Version mode (data present): the pill shows the COMPUTED value — a
  // calculation is derived, the sender never edits it. An operand that is
  // still empty leaves the fixed label in place. Authoring mode (no data):
  // always the label.
  const resolve = (token: string): number | undefined =>
    parseNumericOperand(getVariableValue(data, token));
  return (name) => {
    const formula = formulas[name];
    if (!formula) return undefined;
    const text = data
      ? computeCalcPillDisplay(formula, resolve, CALC_PILL_LABEL).text
      : CALC_PILL_LABEL;
    return { text, title: `=${formula}`, className: VARIABLE_PILL_CALC_CLASS };
  };
};
